import './CandidatePicker.css';
import type { RefObject } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { PartyTag } from '../../components/PartyTag';
import { Portrait } from '../../components/Portrait';
import { t } from '../../copy/strings';
import { fmtPct } from '../../format/numbers';
import type { Candidate } from '../../model/types';
import { setMapCandidate, togglePicker } from '../../state/actions';
import { partyOf } from '../../theme/parties';

export interface CandidatePickerProps {
  /** Id do listbox (o botão Candidato aponta para ele com `aria-controls`). */
  id: string;
  /** Par [A, B] na ordem fixa do número (§9.3: neutralidade, nunca por votos). */
  pair: readonly [Candidate, Candidate];
  /** Opção selecionada (lado do candidato do modo). */
  selected: 0 | 1;
  /** Fatias nacionais; null antes dos votos (a coluna fica vazia). */
  shares: readonly [number, number] | null;
  photos: boolean;
  /** Altura máxima: altura do retângulo do mapa − 12 (§9.3, AC-9.3-17). */
  maxHeight?: number;
  /** Botão Candidato: clicar nele não conta como "clique fora". */
  anchor: RefObject<HTMLElement>;
}

/**
 * Seletor do modo Candidato (§9.3): popover `role="listbox"` com `aria-activedescendant`, 2 opções em ordem fixa
 * pelo número ({A} em cima). Ao abrir, o foco vai para o listbox com a opção selecionada ativa. ↑/↓ movem; Home/End
 * vão às pontas; Enter ou Espaço escolhem (`setMapCandidate`, nova entrada no histórico) e fecham; o foco volta ao
 * botão (MapModeBar). Fecha também com Esc (cascata de a11y/escape.ts, camada `candidatePicker`), clique fora e Tab.
 */
export function CandidatePicker({ id, pair, selected, shares, photos, maxHeight, anchor }: CandidatePickerProps) {
  const [active, setActive] = useState<0 | 1>(selected);
  const listRef = useRef<HTMLDivElement>(null);
  const optionId = (i: number) => `${id}-option-${i}`;

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.focus({ preventScroll: true });
    list.querySelector<HTMLElement>(`[data-index="${selected}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  // Clique fora (fase de captura): fecha sem mover o foco.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (listRef.current?.contains(target) || anchor.current?.contains(target)) return;
      togglePicker(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [anchor]);

  const choose = (i: 0 | 1) => {
    togglePicker(false);
    setMapCandidate(pair[i].number);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    let next: 0 | 1 | null = null;
    switch (e.key) {
      case 'ArrowDown':
        next = 1;
        break;
      case 'ArrowUp':
        next = 0;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = 1;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(active);
        return;
      default:
        return;
    }
    e.preventDefault();
    setActive(next);
    listRef.current?.querySelector<HTMLElement>(`[data-index="${next}"]`)?.scrollIntoView?.({ block: 'nearest' });
  };

  // Tab (perda de foco para fora do seletor) fecha e segue.
  const onFocusOut = (e: FocusEvent) => {
    const to = e.relatedTarget as Node | null;
    if (to && (listRef.current?.contains(to) || anchor.current?.contains(to))) return;
    if (to) togglePicker(false);
  };

  return (
    <div
      ref={listRef}
      id={id}
      class="candidate-picker"
      role="listbox"
      tabIndex={-1}
      aria-label={t('map.picker.aria')}
      aria-activedescendant={optionId(active)}
      data-testid="candidate-picker"
      style={maxHeight !== undefined ? { maxHeight: `${Math.max(0, maxHeight)}px` } : undefined}
      onKeyDown={onKeyDown}
      onFocusOut={onFocusOut}
    >
      {pair.map((c, i) => {
        const pct = shares ? fmtPct(shares[i] as number) : null;
        const party = partyOf(c);
        return (
          // biome-ignore lint/a11y/useFocusableInteractive: listbox com aria-activedescendant (§9.3): o foco fica no listbox, nunca na opção
          // biome-ignore lint/a11y/useKeyWithClickEvents: as teclas são do listbox (↑ ↓ Home End Enter Espaço, onKeyDown)
          <div
            key={c.number}
            id={optionId(i)}
            class={`candidate-picker__option${i === active ? ' is-active' : ''}`}
            role="option"
            data-index={i}
            aria-selected={i === selected}
            aria-label={
              pct === null
                ? undefined
                : t('map.picker.option.aria', { NOME: c.display_name, SIGLA: party.display, NUM: c.number, P: pct })
            }
            onPointerEnter={() => setActive(i as 0 | 1)}
            onClick={() => choose(i as 0 | 1)}
          >
            <Portrait candidate={c} photo={photos ? c.photo : null} size={32} shape="round" />
            <span class="candidate-picker__who">
              <span class="candidate-picker__name">{c.display_name}</span>
              <PartyTag candidate={c} size="sm" />
            </span>
            <span class="candidate-picker__pct">{pct ?? ''}</span>
          </div>
        );
      })}
    </div>
  );
}
